import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { defer, from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthService } from '@ee/common/services';
import { HelpArticleDetail, HelpArticleSummary } from './models/help-article.model';

/**
 * Reads help articles from easyeviction-service. When the visitor has a Cognito token we hit the
 * authenticated endpoints (PUBLIC + the caller's active-org tier); otherwise the public endpoints
 * (PUBLIC only). Works in both the standalone help app and inside the portal (where a token is always
 * present, so the user's currently-selected account type is reflected automatically).
 *
 * `api_prefix` is injected via the 'environment' token (the established @ee/common pattern, cf.
 * PricingService) so the same service runs in every app.
 */
@Injectable({ providedIn: 'root' })
export class HelpService {
  private readonly base: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    @Inject('environment') private environment: any
  ) {
    this.base = `${this.environment.api_prefix}api/help`;
  }

  isAuthenticated(): Observable<boolean> {
    return this.authed();
  }

  list(category?: string): Observable<HelpArticleSummary[]> {
    return this.authed().pipe(
      switchMap((authed) => {
        let params = new HttpParams();
        if (category) {
          params = params.set('category', category);
        }
        return this.http.get<HelpArticleSummary[]>(this.url(authed, '/articles'), { params });
      })
    );
  }

  get(slug: string): Observable<HelpArticleDetail> {
    return this.authed().pipe(
      switchMap((authed) => this.http.get<HelpArticleDetail>(this.url(authed, `/articles/${encodeURIComponent(slug)}`)))
    );
  }

  search(query: string): Observable<HelpArticleSummary[]> {
    return this.authed().pipe(
      switchMap((authed) =>
        this.http.get<HelpArticleSummary[]>(this.url(authed, '/search'), {
          params: new HttpParams().set('q', query),
        })
      )
    );
  }

  /** Best-effort page-view ping (fire-and-forget; the backend resolves the article under the caller's tier). */
  recordView(slug: string): void {
    this.authed()
      .pipe(switchMap((authed) => this.http.post(this.url(authed, `/articles/${encodeURIComponent(slug)}/view`), {})))
      .subscribe({ error: () => undefined });
  }

  /** Records a "was this helpful?" vote, with an optional free-text comment on a 'no'. */
  submitFeedback(slug: string, helpful: boolean, comment?: string): Observable<void> {
    return this.authed().pipe(
      switchMap((authed) =>
        this.http.post<void>(this.url(authed, `/articles/${encodeURIComponent(slug)}/feedback`), {
          helpful,
          comment: comment ?? null,
        })
      )
    );
  }

  /**
   * Re-checks the current Cognito token on every subscription (deferred, not cached), so a sign-in/out is
   * reflected immediately rather than pinned to the value captured when the service was first constructed.
   */
  private authed(): Observable<boolean> {
    return defer(() => from(this.authService.getAccessToken().catch(() => null))).pipe(map((token) => !!token));
  }

  private url(authed: boolean, suffix: string): string {
    return authed ? `${this.base}${suffix}` : `${this.base}/public${suffix}`;
  }
}
